/**
 * 長期記憶ドメインスキーマ（SSOT）
 *
 * ワークスペース単位の長期記憶機能で扱う記憶レコード・インデックス射影・
 * opt-in 設定を単一の真実源として定義する。
 *
 * 鮮度・状態用の追加メタデータは持たず、鮮度は updatedAt と想起時の併記で
 * 表現する（スキーマ最小・要件 11.4）。
 */
import { z } from 'zod';
// ========================================
// カテゴリ区分
// ========================================
/**
 * 記憶カテゴリ。
 * - remaining-tasks / progress-result: 引き継ぎ状態（主目的）
 * - lesson: 教訓（Lessons Learned の吸収先）
 * - note: 補助的なメモ
 */
export const MemoryCategorySchema = z.enum([
    'remaining-tasks',
    'progress-result',
    'lesson',
    'note',
]);
// ========================================
// 記憶レコード（正本）
// ========================================
/**
 * 記憶レコード。slug を自然キーとし、同一 slug は上書き（upsert）される。
 * - summary: 1 行要約（インデックス注入対象）
 * - body: 本体（オンデマンド取得対象）
 * - updatedAt: ISO 8601 更新日時（CAS トークン兼鮮度表現）
 */
export const MemoryRecordSchema = z.object({
    slug: z.string().min(1),
    category: MemoryCategorySchema,
    summary: z.string().min(1),
    body: z.string(),
    updatedAt: z.string(),
});
// ========================================
// インデックス射影（本文を除く）
// ========================================
/**
 * インデックスエントリ。MemoryRecord から body を除いた射影。
 * セッション開始時にシステムプロンプトへ常時注入される想起の表面。
 */
export const MemoryIndexEntrySchema = MemoryRecordSchema.omit({ body: true });
// ========================================
// opt-in 設定
// ========================================
/** maxEntries の既定値・下限・上限 */
export const MEMORY_MAX_ENTRIES_DEFAULT = 50;
export const MEMORY_MAX_ENTRIES_MIN = 5;
export const MEMORY_MAX_ENTRIES_MAX = 200;
/**
 * ワークスペース長期記憶設定スキーマ。
 * null: 記憶機能無効（既定・opt-in）、オブジェクト: enabled で有効/無効を切替。
 */
export const MemoryConfigSchema = z
    .object({
    /** 長期記憶の有効/無効 */
    enabled: z.boolean(),
    /** 記憶エントリ上限（既定: 50、範囲: 5〜200） */
    maxEntries: z
        .number()
        .int()
        .min(MEMORY_MAX_ENTRIES_MIN)
        .max(MEMORY_MAX_ENTRIES_MAX)
        .default(MEMORY_MAX_ENTRIES_DEFAULT),
})
    .nullable();
/**
 * 記憶設定を解決済みの状態に正規化する共有ヘルパー。
 * 未設定（null / undefined）は「無効」として解釈する（opt-in）。
 * 消費側（実行基盤のツール組み立て・設定 UI）で再利用する。
 */
export function resolveMemoryConfig(config) {
    if (config == null) {
        return { enabled: false, maxEntries: MEMORY_MAX_ENTRIES_DEFAULT };
    }
    return { enabled: config.enabled, maxEntries: config.maxEntries };
}
//# sourceMappingURL=memory-schemas.js.map